import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { authKeys } from './keys';

import type { AdminResponse, DelegatePrincipal } from '@delegation/contracts';

import { http } from '@/shared/api/http';
import { useAuthStore, type AuthUser } from '@/shared/stores/authStore';

export interface MeResponse {
  type: 'admin' | 'delegated_user';
  admin?: AdminResponse;
  delegatedUser?: DelegatePrincipal;
}

export async function fetchMe(): Promise<MeResponse> {
  return http.get('auth/me').json<MeResponse>();
}

/// Map the `/auth/me` principal onto the auth-store user. Mirrors the shapes the
/// admin + delegate login mutations build, so a boot-time rehydration produces
/// an identical session. Returns null for an unexpected/empty payload.
export function meToAuthUser(me: MeResponse): AuthUser | null {
  if (me.type === 'admin' && me.admin) {
    return {
      id: me.admin.id,
      role: 'admin',
      adminRole: me.admin.role,
      fullName: me.admin.fullName,
    };
  }
  if (me.type === 'delegated_user' && me.delegatedUser) {
    return {
      id: me.delegatedUser.id,
      role: 'delegated',
      fullName: me.delegatedUser.fullName,
      units: me.delegatedUser.units,
      mustChangePassword: me.delegatedUser.mustChangePassword,
    };
  }
  return null;
}

/**
 * Subscribes to the current authenticated principal. Disabled by default
 * until an access token is in memory — the http client refresh-on-401
 * interceptor will keep it fresh once login (or app-boot rehydration via
 * refresh cookie) has populated the token.
 */
export function useMeQuery(): UseQueryResult<MeResponse> {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: fetchMe,
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}
